import { useState, useEffect } from 'react'
import { Key, Plus, Shield, CheckCircle, XCircle, Trash2, AlertTriangle, Eye, EyeOff, BarChart3 } from 'lucide-react'
import PlatformKeysAnalytics from './PlatformKeysAnalytics'

interface PlatformKeyAllowlistEntry {
  tenantId: string
  allowedKeys: string[]
  enabled: boolean
  monthlyLimit?: number
  currentMonthUsage?: number
  expiresAt?: string
  createdBy: string
  createdAt: string
  updatedAt: string
  notes?: string
}

interface PlatformSecret {
  key: 'claudeApiKey' | 'openaiApiKey'
  label: string
  description: string
  isSet: boolean
}

const PlatformKeysManager = () => {
  const [subTab, setSubTab] = useState<'keys' | 'analytics'>('keys')
  const [allowlist, setAllowlist] = useState<PlatformKeyAllowlistEntry[]>([])
  const [secrets, setSecrets] = useState<PlatformSecret[]>([
    { key: 'claudeApiKey', label: 'Claude API Key', description: 'Anthropic Claude API key for AI features', isSet: false },
    { key: 'openaiApiKey', label: 'OpenAI API Key', description: 'OpenAI API key for AI features', isSet: false }
  ])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modals
  const [showSetSecretModal, setShowSetSecretModal] = useState(false)
  const [showAuthorizeTenantModal, setShowAuthorizeTenantModal] = useState(false)

  // Form state
  const [selectedSecretKey, setSelectedSecretKey] = useState<'claudeApiKey' | 'openaiApiKey'>('claudeApiKey')
  const [secretValue, setSecretValue] = useState('')
  const [showSecretValue, setShowSecretValue] = useState(false)
  const [authForm, setAuthForm] = useState({
    tenantId: '',
    allowedKeys: [] as string[],
    monthlyLimit: '',
    notes: '',
    allowAll: false
  })

  useEffect(() => {
    loadAllowlist()
  }, [])

  const loadAllowlist = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/admin/platform-keys', {
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        setAllowlist(data.allowlist || [])

        // Update secrets status if provided
        if (data.secrets) {
          setSecrets(prevSecrets => prevSecrets.map(s => ({
            ...s,
            isSet: data.secrets[s.key] === true
          })))
        }
      } else {
        throw new Error('Failed to load allowlist')
      }
    } catch (err) {
      console.error('Error loading allowlist:', err)
      setError(err instanceof Error ? err.message : 'Failed to load allowlist')
    } finally {
      setLoading(false)
    }
  }

  const handleSetSecret = async () => {
    if (!secretValue.trim()) {
      alert('Please enter a secret value')
      return
    }

    try {
      const response = await fetch('/api/admin/platform-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'set-secret',
          secretKey: selectedSecretKey,
          secretValue: secretValue.trim()
        })
      })

      if (response.ok) {
        // Update the isSet status
        setSecrets(prevSecrets => prevSecrets.map(s =>
          s.key === selectedSecretKey ? { ...s, isSet: true } : s
        ))
        setShowSetSecretModal(false)
        setSecretValue('')
        alert('Platform secret set successfully')
      } else {
        let errorMessage = `HTTP ${response.status}`
        try {
          const error = await response.json()
          errorMessage = error.error || errorMessage
        } catch (parseErr) {
          console.error('[PlatformKeysManager] Failed to parse error response:', parseErr)
          // Use generic message
        }
        console.error('[PlatformKeysManager] Set secret failed:', errorMessage)
        throw new Error(errorMessage)
      }
    } catch (err) {
      console.error('Error setting secret:', err)
      alert(err instanceof Error ? err.message : 'Failed to set secret')
    }
  }

  const handleAuthorizeTenant = async () => {
    // Use "*" for wildcard (all tenants)
    const tenantId = authForm.allowAll ? '*' : authForm.tenantId.trim()

    if (!tenantId || authForm.allowedKeys.length === 0) {
      alert('Please enter a tenant ID and select at least one key')
      return
    }

    try {
      const response = await fetch('/api/admin/platform-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'authorize-tenant',
          tenantId,
          allowedKeys: authForm.allowedKeys,
          monthlyLimit: authForm.monthlyLimit ? parseInt(authForm.monthlyLimit) : undefined,
          notes: authForm.notes.trim() || undefined
        })
      })

      if (response.ok) {
        setShowAuthorizeTenantModal(false)
        setAuthForm({ tenantId: '', allowedKeys: [], monthlyLimit: '', notes: '', allowAll: false })
        loadAllowlist()
        alert(authForm.allowAll ? 'All tenants authorized successfully' : 'Tenant authorized successfully')
      } else {
        let errorMessage = `HTTP ${response.status}`
        try {
          const error = await response.json()
          errorMessage = error.error || errorMessage
        } catch (parseErr) {
          console.error('[PlatformKeysManager] Failed to parse error response:', parseErr)
          // Use generic message
        }
        console.error('[PlatformKeysManager] Authorize tenant failed:', errorMessage)
        throw new Error(errorMessage)
      }
    } catch (err) {
      console.error('Error authorizing tenant:', err)
      alert(err instanceof Error ? err.message : 'Failed to authorize tenant')
    }
  }

  const handleRevokeTenant = async (tenantId: string) => {
    if (!confirm(`Are you sure you want to revoke platform key access for ${tenantId}?`)) {
      return
    }

    try {
      const response = await fetch('/api/admin/platform-keys', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'revoke-tenant',
          tenantId
        })
      })

      if (response.ok) {
        loadAllowlist()
        alert('Tenant access revoked successfully')
      } else {
        let errorMessage = `HTTP ${response.status}`
        try {
          const error = await response.json()
          errorMessage = error.error || errorMessage
        } catch (parseErr) {
          console.error('[PlatformKeysManager] Failed to parse error response:', parseErr)
          // Use generic message
        }
        console.error('[PlatformKeysManager] Revoke access failed:', errorMessage)
        throw new Error(errorMessage)
      }
    } catch (err) {
      console.error('Error revoking access:', err)
      alert(err instanceof Error ? err.message : 'Failed to revoke access')
    }
  }

  const toggleKeySelection = (key: string) => {
    setAuthForm({
      ...authForm,
      allowedKeys: authForm.allowedKeys.includes(key)
        ? authForm.allowedKeys.filter(k => k !== key)
        : [...authForm.allowedKeys, key]
    })
  }

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="bg-white rounded-lg border border-gray-200 p-2">
        <div className="flex gap-2">
          <button
            onClick={() => setSubTab('keys')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              subTab === 'keys'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Key className="w-4 h-4" />
            API Keys
          </button>
          <button
            onClick={() => setSubTab('analytics')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              subTab === 'analytics'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
          </button>
        </div>
      </div>

      {/* Analytics Tab */}
      {subTab === 'analytics' && <PlatformKeysAnalytics />}

      {/* Keys Tab */}
      {subTab === 'keys' && (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Key className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Platform API Keys</h2>
              <p className="text-sm text-gray-600">Manage shared API keys for all tenants</p>
            </div>
          </div>
        </div>

        {/* Platform Secrets */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {secrets.map((secret) => (
            <div key={secret.key} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{secret.label}</h3>
                  <p className="text-sm text-gray-600 mt-1">{secret.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {secret.isSet ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                        <CheckCircle className="w-3 h-3" />
                        Configured
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                        <AlertTriangle className="w-3 h-3" />
                        Not configured
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedSecretKey(secret.key)
                    setShowSetSecretModal(true)
                  }}
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg"
                >
                  {secret.isSet ? 'Update' : 'Set Key'}
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => setShowAuthorizeTenantModal(true)}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Authorize Tenant
        </button>
      </div>

      {/* Allowlist */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Authorized Tenants</h3>
          <p className="text-sm text-gray-600 mt-1">Tenants allowed to use platform API keys</p>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading...</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : allowlist.length === 0 ? (
          <div className="p-6 text-center">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No tenants authorized yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {allowlist.map((entry) => (
              <div key={entry.tenantId} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-900">{entry.tenantId}</h4>
                      {entry.enabled ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                          <XCircle className="w-3 h-3" />
                          Disabled
                        </span>
                      )}
                    </div>

                    <div className="space-y-1 text-sm text-gray-600">
                      <p><strong>Allowed Keys:</strong> {entry.allowedKeys.join(', ')}</p>
                      {entry.monthlyLimit && (
                        <p>
                          <strong>Monthly Limit:</strong> {entry.currentMonthUsage || 0} / {entry.monthlyLimit} calls
                        </p>
                      )}
                      <p><strong>Created By:</strong> {entry.createdBy}</p>
                      <p><strong>Created:</strong> {new Date(entry.createdAt).toLocaleString()}</p>
                      {entry.notes && <p><strong>Notes:</strong> {entry.notes}</p>}
                    </div>
                  </div>

                  <button
                    onClick={() => handleRevokeTenant(entry.tenantId)}
                    className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
                    title="Revoke Access"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Set Secret Modal */}
      {showSetSecretModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Set {secrets.find(s => s.key === selectedSecretKey)?.label}
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key Value
              </label>
              <div className="relative">
                <input
                  type={showSecretValue ? 'text' : 'password'}
                  value={secretValue}
                  onChange={(e) => setSecretValue(e.target.value)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter API key..."
                />
                <button
                  type="button"
                  onClick={() => setShowSecretValue(!showSecretValue)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecretValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                This will be encrypted before storage
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSetSecret}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Save Key
              </button>
              <button
                onClick={() => {
                  setShowSetSecretModal(false)
                  setSecretValue('')
                  setShowSecretValue(false)
                }}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Authorize Tenant Modal */}
      {showAuthorizeTenantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Authorize Tenant</h3>

            <div className="space-y-4 mb-6">
              {/* Allow All Checkbox */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={authForm.allowAll}
                    onChange={(e) => setAuthForm({ ...authForm, allowAll: e.target.checked, tenantId: '' })}
                    className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-blue-900">Allow ALL tenants (wildcard)</span>
                    <p className="text-xs text-blue-700 mt-1">
                      This will authorize all current and future tenants to use the selected API keys
                    </p>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tenant ID {authForm.allowAll && <span className="text-xs text-gray-500">(disabled for wildcard)</span>}
                </label>
                <input
                  type="text"
                  value={authForm.allowAll ? '* (all tenants)' : authForm.tenantId}
                  onChange={(e) => setAuthForm({ ...authForm, tenantId: e.target.value })}
                  disabled={authForm.allowAll}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                  placeholder="example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allowed API Keys
                </label>
                <div className="space-y-2">
                  {secrets.map((secret) => (
                    <label key={secret.key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={authForm.allowedKeys.includes(secret.key)}
                        onChange={() => toggleKeySelection(secret.key)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{secret.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Limit (optional)
                </label>
                <input
                  type="number"
                  value={authForm.monthlyLimit}
                  onChange={(e) => setAuthForm({ ...authForm, monthlyLimit: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 10000"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum API calls per month</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={authForm.notes}
                  onChange={(e) => setAuthForm({ ...authForm, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                  placeholder="Why this tenant was granted access..."
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleAuthorizeTenant}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Authorize
              </button>
              <button
                onClick={() => {
                  setShowAuthorizeTenantModal(false)
                  setAuthForm({ tenantId: '', allowedKeys: [], monthlyLimit: '', notes: '', allowAll: false })
                }}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      )}
    </div>
  )
}

export default PlatformKeysManager
